import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Home, Briefcase, BookOpen, Info, Mail, Wrench, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const STATIC_PAGES = [
  { to: "/", label: "Home", Icon: Home },
  { to: "/services", label: "Services", Icon: Wrench },
  { to: "/work", label: "Case studies", Icon: Briefcase },
  { to: "/insights", label: "Insights", Icon: BookOpen },
  { to: "/about", label: "About", Icon: Info },
  { to: "/contact", label: "Contact", Icon: Mail },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [cases, setCases] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  // Global keyboard shortcut: Cmd/Ctrl+K or '/'
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      const metaOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      if ((metaOrCtrl && e.key.toLowerCase() === "k") ||
          (e.key === "/" && !isEditing(e.target))) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lazy-load content lists on first open
  useEffect(() => {
    if (!open || loaded) return;
    (async () => {
      try {
        const [c, p] = await Promise.all([api.get("/cases"), api.get("/posts")]);
        setCases(c.data);
        setPosts(p.data);
      } catch { /* empty */ }
      setLoaded(true);
    })();
  }, [open, loaded]);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <Command className="bg-[#0A0A0C] text-white">
          <CommandInput placeholder="Search pages, case studies, posts…" data-testid="command-palette-input" className="text-white placeholder:text-white/40" />
          <CommandList className="max-h-[60vh]">
            <CommandEmpty>Nothing matches.</CommandEmpty>
            <CommandGroup heading="Pages">
              {STATIC_PAGES.map(({ to, label, Icon }) => (
                <CommandItem
                  key={to}
                  value={`${label} ${to}`}
                  onSelect={() => go(to)}
                  data-testid={`cp-page-${to}`}
                  className="text-white/80 aria-selected:bg-[#00F0FF]/10 aria-selected:text-[#00F0FF]"
                >
                  <Icon className="w-4 h-4 text-[#00F0FF]" /> {label}
                  <span className="ml-auto text-xs font-mono-neo text-white/40">{to}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {cases.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Case studies">
                  {cases.map((c) => (
                    <CommandItem
                      key={c.slug}
                      value={`${c.title} ${c.category} ${c.tagline}`}
                      onSelect={() => go(`/work/${c.slug}`)}
                      className="text-white/80 aria-selected:bg-[#00F0FF]/10 aria-selected:text-[#00F0FF]"
                    >
                      <Briefcase className="w-4 h-4 text-[#FF00FF]" /> {c.title}
                      <span className="ml-auto text-xs font-mono-neo text-white/40">{c.category}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
            {posts.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Insights">
                  {posts.map((p) => (
                    <CommandItem
                      key={p.slug}
                      value={`${p.title} ${p.category} ${p.excerpt}`}
                      onSelect={() => go(`/insights/${p.slug}`)}
                      className="text-white/80 aria-selected:bg-[#00F0FF]/10 aria-selected:text-[#00F0FF]"
                    >
                      <Sparkles className="w-4 h-4 text-[#39FF14]" /> {p.title}
                      <span className="ml-auto text-xs font-mono-neo text-white/40">{p.read_time}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}

function isEditing(el) {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || el.isContentEditable;
}
